import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "hooks";
import SensitiveAmount from "components/sensitive-amount/SensitiveAmount";
import Notice from "components/tw/notice";
import { btnLinkCls, btnSecondaryCls } from "components/tw/classes";
import { openPdfPreview } from "components/modalx/modalx-openers";
import { QueueSkeleton } from "features/cabinet-queues/queue-list-states";
import { useQueueData } from "features/cabinet-queues/use-queue-data";
import { Fact, PlannedSalaryChangeFact, panelClass } from "features/personnel/PersonnelCardParts";
import { contractTypeKey, formatDay } from "features/personnel/personnel-helpers";
import { fetchMyWork, MyWork } from "services/cabinet-me.service";

// The links of this block are plain text lines; on a phone each gets a 44 px tap area.
const PHONE_TAP_TARGET = "max-md:min-h-11";

/** Pareigos, padalinys, vadovas: what the person has even before any contract is on file. */
function PlaceFacts({ work }: { work: MyWork }) {
  const { t } = useTranslation();
  const departmentNames = work.departments.map((department) => department.name).join(", ");
  return (
    <>
      <Fact label={t("positions_title")} value={work.positionsText} />
      <Fact label={t("cabinet_work_department")} value={departmentNames} />
      <Fact label={t("manager")} value={work.manager?.name} />
    </>
  );
}

/** The contract, its signed amendments and the way to all the hiring documents. */
function ContractLinks({ work, companyId, userId }: { work: MyWork; companyId: string; userId: string }) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { contract, amendments } = work;

  return (
    <div className="mt-4 space-y-3 border-t border-gray-100 pt-3 text-sm">
      <div className="flex flex-wrap gap-x-6 gap-y-2">
        {contract && (
          <button type="button" className={`${btnLinkCls} ${PHONE_TAP_TARGET}`} onClick={() => openPdfPreview(dispatch, contract.fileId, companyId)}>
            {t("cabinet_work_contract")}
          </button>
        )}
        <Link to={`/app/${companyId}/profile/${userId}/files`} className={`${btnLinkCls} ${PHONE_TAP_TARGET}`}>
          {t("cabinet_work_all_documents")}
        </Link>
      </div>

      {amendments.length > 0 && (
        <div>
          <h3 className="mb-1 font-medium text-gray-900">{t("cabinet_work_amendments", { total: amendments.length })}</h3>
          <ul className="divide-y divide-gray-100">
            {amendments.map(({ id, effectiveDate, isEmploymentTermsChange, fileId }) => (
              <li key={id} className="flex flex-wrap items-center justify-between gap-2 py-1.5">
                <span className="text-gray-700">
                  {formatDay(effectiveDate)}
                  {isEmploymentTermsChange && ` · ${t("cabinet_work_terms_change")}`}
                </span>
                {fileId && (
                  <button type="button" className={`${btnLinkCls} ${PHONE_TAP_TARGET}`} onClick={() => openPdfPreview(dispatch, fileId, companyId)}>
                    {t("view")}
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/** What the person's employment says, once the answer is in. */
function WorkFacts({ work, companyId, userId }: { work: MyWork; companyId: string; userId: string }) {
  const { t } = useTranslation();
  const { terms } = work;

  // Without an onboarding or employment record there is nothing more to tell.
  if (!terms) {
    return (
      <>
        <div className="grid gap-x-8 gap-y-1 text-sm text-gray-700 md:grid-cols-2">
          <PlaceFacts work={work} />
        </div>
        <p className="mt-3 text-sm text-gray-500">{t("cabinet_work_no_terms")}</p>
      </>
    );
  }

  const contractType = contractTypeKey(terms.contractType);
  return (
    <>
      <div className="grid gap-x-8 gap-y-1 text-sm text-gray-700 md:grid-cols-2">
        <PlaceFacts work={work} />
        <Fact label={t("cabinet_work_since")} value={formatDay(work.hiringDate)} />
        <Fact label={t("contract_type")} value={contractType ? t(contractType) : ""} />
        {terms.contractEndDate && (
          <Fact label={t("cabinet_work_contract_until")} value={formatDay(terms.contractEndDate)} />
        )}
        <Fact label={t("cabinet_work_hours")} value={terms.hoursPerWeek} />
        {terms.probationEndDate && (
          <Fact label={t("cabinet_work_probation_until")} value={formatDay(terms.probationEndDate)} />
        )}
        {/* The eye of the salary is a 14 px icon from a shared component; on a phone it gets a padded, 46 px tap area from here. */}
        <Fact
          label={t("personnel_salary")}
          value={
            <span className="max-md:[&_button]:-m-4 max-md:[&_button]:p-4">
              <SensitiveAmount value={terms.salary} />
            </span>
          }
        />
        {terms.plannedSalaryChange && <PlannedSalaryChangeFact change={terms.plannedSalaryChange} />}
      </div>
      <ContractLinks work={work} companyId={companyId} userId={userId} />
    </>
  );
}

/**
 * The «Darbas» block of «Apie mane»: the person's own employment, read only. The salary stays
 * behind the eye until the person asks for it. A failed request shows one line with a retry
 * and leaves the rest of the page as it is.
 */
export default function CabinetWork() {
  const { t } = useTranslation();
  const { companyId = "", userId = "" } = useParams();
  const work = useQueueData(() => fetchMyWork(companyId), { scopeKey: companyId });

  return (
    <section className={panelClass}>
      <h2 className="mb-3 text-base font-semibold text-gray-900">{t("cabinet_work_title")}</h2>
      {work.data && <WorkFacts work={work.data} companyId={companyId} userId={userId} />}
      {!work.data && work.hasFailed && (
        <Notice tone="critical" title={t("cabinet_work_load_failed")}>
          <button type="button" className={btnSecondaryCls} onClick={work.reload}>
            {t("try_again")}
          </button>
        </Notice>
      )}
      {!work.data && !work.hasFailed && <QueueSkeleton />}
    </section>
  );
}
